import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';
import FormProvider, { RHFAutocomplete } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { getRequest, postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import PayrollSlipExact from './genrate-payroll-issue-payment-accodian-card';
import { formatDate } from '../../risk-register/feching';
import { enqueueSnackbar } from 'notistack';

const GeneratePayrollIssuePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { id } = useParams();
  const profileData = state?.data || {};
  const payrollId = profileData?.id;
  const [showPayrollSlip, setShowPayrollSlip] = useState(false);
  const [isGeneratingSlip, setIsGeneratingSlip] = useState(false);
  const [payrollData, setPayrollData] = useState({});
  const [employeeData, setEmployeeData] = useState([]);

  const { employeeId } = location.state || {};
  const [employees, setEmployees] = useState([]);
  const [bankInformation, setBankInformation] = useState([]);

  useEffect(() => {
    if (employeeId) {
      handleGeneratePayrollSlip(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployees();
    getBankInformation();
  }, []);

  const fetchEmployees = async () => {
    const res = await getRequest(endpoints.accounting.employee.get_list);
    if (res) setEmployees(res);
  };

  const getBankInformation = async () => {
    const data = await getRequest(endpoints.accounting.bank_information.get_list);
    if (data) {
      setBankInformation(data);
    }
  };
  const validationSchema = Yup.object().shape({
    date: Yup.date().required('Date is required').typeError('Invalid date'),
    payment_method: Yup.string().required('Payment method is required'),
    bank_information_id: Yup.object()
      .shape({
        id: Yup.number().required(),
        bank_name: Yup.string().required(),
      })
      .nullable()
      .required('Bank name is required'),
  });
  const defaultValues = {
    date: new Date(),
  };
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });
  const {
    reset,
    control,
    clearErrors,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const formData = new FormData();
      let employeeIds = data.employee_ids;
      if (!employeeIds?.length && employeeId) {
        employeeIds = [employeeId];
      }
      if (Array.isArray(employeeIds)) {
        employeeIds.forEach((empId, index) => {
          formData.append(`employee_data[${index}][employee_id]`, empId);
          formData.append(
            `employee_data[${index}][bank_information_id]`,
            data.bank_information_id?.id
          );
          formData.append(`employee_data[${index}][payment_method]`, data.payment_method);
          formData.append(`employee_data[${index}][date]`, formatDate(data.date));
        });
      }
      const url = payrollId
        ? `${endpoints.accounting.payroll.generate_payroll.create_issue_payroll}/${id}/payment?_method=PUT`
        : `${endpoints.accounting.payroll.generate_payroll.create_issue_payroll}/${id}/payment`;

      const response = await postRequest(url, formData, true);
      if (response?.errors) {
        const errorMessages = Object.values(response.errors).flat();
        errorMessages.forEach((msg) => enqueueSnackbar(msg, { variant: 'error' }));
        return; // Stop navigation on error
      }
      enqueueSnackbar('Payroll issued successfully!', { variant: 'success' });
      reset();
      navigate('/accounts-management/payroll/genrate-payroll');
    } catch (err) {
      console.error('Error submitting form:', err);
      enqueueSnackbar('Something went wrong while submitting the form.', { variant: 'error' });
    }
  });
  const handleBack = () => {
    navigate('/accounts-management/payroll/genrate-payroll');
  };
  const handleGeneratePayrollSlip = async (employeeId) => {
    try {
      setIsGeneratingSlip(true);
      const formValues = getValues();
      const payload = {
        date: formValues.date,
        payment_method: formValues.payment_method,
        bank_information_id: formValues.bank_information_id?.id || null,
        employee_ids: formValues.employee_ids || [employeeId] || [],
      };
      const generateSlipUrl = `${endpoints.accounting.payroll.generate_payroll.preview_slip}/${id}/pay-slips`;
      const response = await postRequest(generateSlipUrl, payload);

      if (response && response.metadata && response.metadata.success) {
        const employeeData = response.payload || [];
        const payrollData = {
          payroll_number: id,
          payment_method: formValues.payment_method || 'Cash',
          payroll_period: employeeData[0]?.payroll_tax_details?.[0]?.pay_period || 'weekly',
        };
        setPayrollData(payrollData);
        setEmployeeData(employeeData);
        setShowPayrollSlip(true);
      } else {
        enqueueSnackbar(response.message);
      }
    } catch (error) {
      console.error('Error generating payroll slip:', error);
    } finally {
      setIsGeneratingSlip(false);
    }
  };
  return (
    <Box sx={{ ml: 2 }}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h5">ISSUE PAYMENT</Typography>
          <Button
            onClick={handleBack}
            variant="contained"
            startIcon={<Iconify icon="material-symbols:arrow-back" />}
            className="dark-button"
          >
            Back
          </Button>
        </Box>
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent
            sx={{
              bgcolor: '#919EAB14',
              boxShadow:
                'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
            }}
          >
            <Box
              mt={2}
              mb={2}
              gap={3}
              display="grid"
              gridTemplateColumns={{ xs: 'repeat(3, 1fr)', lg: 'repeat(3, 1fr)' }}
            >
              {!employeeId && (
                <Controller
                  name="employee_ids"
                  control={control}
                  defaultValue={[]}
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    const isAllSelected = value.length === employees.length;
                    const isIndeterminate = value.length > 0 && value.length < employees.length;

                    const handleSelectAllClick = () => {
                      if (isAllSelected) {
                        onChange([]);
                      } else {
                        onChange(employees.map((emp) => emp.id));
                      }
                    };

                    return (
                      <FormControl error={!!error} sx={{ flexShrink: 1, width: { xs: 1, md: 1 } }}>
                        <InputLabel>Employees</InputLabel>
                        <Select
                          multiple
                          value={value}
                          onChange={(event) => {
                            const { value: selected } = event.target;
                            if (selected[selected.length - 1] === 'all') {
                              if (isAllSelected) {
                                onChange([]);
                              } else {
                                onChange(employees.map((emp) => emp.id));
                              }
                            } else {
                              onChange(selected);
                            }
                          }}
                          input={<OutlinedInput label="Employees" />}
                          renderValue={(selected) =>
                            employees
                              .filter((emp) => selected.includes(emp.id))
                              .map((emp) => emp.first_name + ' ' + emp.last_name)
                              .join(', ')
                          }
                          MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                        >
                          <MenuItem value="all">
                            <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
                            Select All
                          </MenuItem>
                          {employees?.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                              <Checkbox checked={value.includes(emp.id)} />
                              {emp.first_name} {emp.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }}
                />
              )}
              <Controller
                name="date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date*"
                    {...field}
                    inputFormat="yyyy-MM-dd"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />

              <RHFAutocomplete
                name="payment_method"
                label="Payment Method"
                options={['Cash', 'Cheque', 'Credit Card', 'Debit Card', 'ACH']}
                getOptionLabel={(option) => option?.name || option}
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id || option}>
                    {option?.name || option}
                  </li>
                )}
              />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Controller
                  name="bank_information_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <RHFAutocomplete
                      {...field}
                      sx={{ width: '100%' }}
                      label="Name of Bank"
                      options={bankInformation}
                      onChange={(_, value) => {
                        field.onChange(value);
                        clearErrors('bank_information_id');
                      }}
                      getOptionLabel={(option) => option?.bank_name || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.bank_name}
                        </li>
                      )}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {showPayrollSlip && (
          <Box p={2}>
            <PayrollSlipExact payrollData={payrollData} employeeData={employeeData} />
          </Box>
        )}

        {!showPayrollSlip && (
          <Box display="flex" justifyContent="flex-end" p={2}>
            <LoadingButton
              onClick={handleGeneratePayrollSlip}
              variant="contained"
              loading={isGeneratingSlip}
              className="dark-button"
            >
              Generate Payroll Slip
            </LoadingButton>
          </Box>
        )}
        {/* Submit Button */}
        {showPayrollSlip && (
          <Box display="flex" justifyContent="flex-end" p={2}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              className="dark-button"
            >
              Send Payment
            </LoadingButton>
          </Box>
        )}
      </FormProvider>
    </Box>
  );
};

export default GeneratePayrollIssuePage;
